import { defaultsDeep as _defaultsDeep } from 'lodash';
import { Button, Icon, Loader } from '@pwc/pwc-deals-ui';
import { Constants, USER_ROLES } from '@pwc/pwc-deals-web-common';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  getIsThirdPartyMember,
  getUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';

import cloneDeep from 'lodash/cloneDeep';
import _isNil from 'lodash/isNil';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { topicPanelType } from 'features/project/ViewProject/ProjectOverview/Topic/mapTopic';
import { RTEContextProvider } from 'features/analyses/Freeform/FreeformAnalysisForm/FreeformAnalysisFormContext';
import EnableForProjectState from 'features/security/EnableForProjectState';
import ShowForPermissions from 'features/security/ShowForPermissions';
import LoadingError from 'common/components/LoadingError/index';
import { insertTablesAndCharts } from 'common/components/RichTextEditor/CkEditorExtensions';
import RightPanel, {
  TOPIC_LAYOUTS,
} from 'common/components/LayoutsRightPanel/RightPanel';
import ModalConfirmation from 'common/components/ModalConfirmation/ModalConfirmation.tsx';
import { getLoggedInUser } from 'common/redux/security/loggedInUser/loggedInUserSelector';
import useTabletOrMobile from 'common/hooks/useTabletOrMobile';
import useIsMountedRef from 'common/hooks/useIsMountedRef';
import useInfiniteScroll from 'common/hooks/useInfiniteScroll';
import useWindowSize from 'common/hooks/useWindowSize';
import { showModalError } from 'common/redux/errors/errorsSlice';
import {
  fetchTopicsByPage,
  fetchSharedTopicsByPage,
  selectAllTopic,
  deleteTopicById,
  removeTopic,
  selectTopicsState,
  moveTopicById,
  addTopic,
  removeAllTopics,
  selectTopicsIsCreating,
  selectTopicsIsUpdating,
  updateTopic,
} from 'common/redux/project-overview/projectOverviewSlice';
import { useSyncedInfo } from 'common/components/Autosync/SyncedInfoProvider.tsx';

import './ProjectOverviewModuleContainer.scss';
import useMobile from '../../../../common/hooks/useMobile';
import { mapOnlyPowerBiTopic } from './utils/helper';
import { getTopic } from '../../topicService';
import ProjectOverviewModule from './ProjectOverviewModule';
import AutoLock from '../../../../common/components/AutoLock';
import featureFlags from '../../../../common/helpers/featureFlags';
import AutoCreateTopic from './Topic/AutoCreateTopic/AutoCreateTopic.tsx';
import { getProjectName } from '../../projectSlice';
import PowerPivotNotSupportedModal from '../../../analyses/common/LibraryItemCard/PowerPivot/components/PowerPivotNotSupportedModal';
import {
  TEXT_AREA,
  UNABLE_TO_ADD_CONTENT,
} from '../../../analyses/common/LibraryItemCard/PowerPivot/constants/PowerPivotErrorMessages';

const ProjectOverviewModuleContainer = ({ sharedTopics }) => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const loggedInUser = useSelector(getLoggedInUser);
  const topicsData = useSelector(selectAllTopic);
  const {
    ids,
    rowCount,
    currentPage,
    loading: topicsIsLoading,
    error: topicsHasError,
  } = useSelector(selectTopicsState);
  const isCreatingTopic = useSelector(selectTopicsIsCreating);
  const isUpdatingTopic = useSelector(selectTopicsIsUpdating);
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);

  const isTabletOrMobile = useTabletOrMobile();
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [isDeleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [showEmptyClipboardModal, setShowEmptyClipboardModal] = useState(false);
  const [deactivateCallback, setDeactivateCallback] = useState(null);

  const projectOverviewText =
    "Showcase the project's most important information by creating 'Project overview' topics.";
  const editorContainerID = 'rich-text-editor';

  const { projectId } = useParams();
  const projectName = useSelector(getProjectName);
  const dispatch = useDispatch();
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const pageSize = 3;

  const [topicsModel, setTopicsModel] = useState({
    pagedTopics: [],
    newTopic: null,
  });
  const [topicsLayout, setTopicsLayout] = useState({});

  const [itemPage, setItemPage] = useState(0);
  const [itemCount, setItemCount] = useState(null);
  const { height } = useWindowSize();
  const isMounted = useIsMountedRef();
  const scrollThreshold = useInfiniteScroll('.project-overview');

  const topics = useMemo(
    () =>
      topicsModel.newTopic
        ? [topicsModel.newTopic, ...topicsModel.pagedTopics]
        : topicsModel.pagedTopics,
    [topicsModel.newTopic, topicsModel.pagedTopics]
  );

  const getTopicToUpdate = (topicID) => {
    // Find topic to remove
    const foundPagedTopic = topicsModel.pagedTopics.find(
      (topic) => topic.content.contentId === topicID
    );
    const foundNewTopic =
      topicsModel.newTopic?.content.contentId === topicID
        ? topicsModel.newTopic
        : undefined;

    // we have to remove item from list of layouts too
    const layoutsCopy = { ...topicsLayout };
    delete layoutsCopy[topicID];

    if (foundPagedTopic) {
      // Remove from pagedTopics if found here
      const remainingPagedTopics = topicsModel.pagedTopics.filter(
        (topic) => topic.content.contentId !== topicID
      );

      return {
        isNewTopic: false,
        topic: foundPagedTopic,
        allTopics: topicsModel.pagedTopics,
        remainingTopics: remainingPagedTopics,
        remainingLayouts: layoutsCopy,
      };
    }

    if (foundNewTopic) {
      return {
        isNewTopic: true,
        topic: foundNewTopic,
        allTopics: [],
        remainingTopics: [],
        remainingLayouts: layoutsCopy,
      };
    }

    return null;
  };

  useEffect(() => {
    setItemCount(rowCount);
  }, [rowCount]);

  const compileTopics = useCallback(
    (pagedTopics = [], rCunt, cPage) => {
      setTopicsModel({
        newTopic: topicsModel.newTopic,
        pagedTopics,
      });

      setItemCount(rCunt);
      setItemPage(cPage);
    },
    [setTopicsModel, topicsModel]
  );

  useEffect(() => {
    if (topicsData) {
      compileTopics(topicsData, rowCount, currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicsData]);

  const fetchPagedTopics = useCallback(
    (page = 0) => {
      if (sharedTopics || !isPwcUser || isThirdPartyMember) {
        dispatch(fetchSharedTopicsByPage({ projectId, page, pageSize }));
      } else {
        dispatch(fetchTopicsByPage({ projectId, page, pageSize }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, projectId, sharedTopics, isPwcUser]
  );

  const canBeScrolled = useCallback(
    () => height && document.body.scrollHeight > height,
    [height]
  );

  const hasMoreItems = useCallback(() => {
    const { pagedTopics = [] } = topicsModel;
    return Boolean(itemCount && itemCount > pagedTopics.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicsModel.pagedTopics, itemCount]);

  useEffect(() => {
    if (isMounted.current) {
      dispatch(removeAllTopics());
      setTopicsModel({
        pagedTopics: [],
        newTopic: null,
      });
      fetchPagedTopics(0);
    }

    return () => {
      dispatch(removeAllTopics());
    };
  }, [dispatch, fetchPagedTopics, isMounted, sharedTopics]);

  useEffect(() => {
    if (
      isMounted.current &&
      topicsModel.pagedTopics &&
      topicsModel.pagedTopics.length > 0 &&
      itemCount
    ) {
      const isScrollable = canBeScrolled();
      const hasMore = hasMoreItems();

      if (!isScrollable && hasMore && !topicsIsLoading) {
        fetchPagedTopics(itemPage + 1);
      }
    }
  }, [
    topicsModel.pagedTopics,
    itemCount,
    itemPage,
    fetchPagedTopics,
    hasMoreItems,
    topicsIsLoading,
    canBeScrolled,
    isMounted,
  ]);

  const handleOnAddNewTopic = useCallback(() => {
    if (!topicsModel.newTopic || topicsModel.newTopic.hasError) {
      let newTopicContentId = 0;
      topics.forEach((topic) => {
        if (topic.content.contentId >= newTopicContentId) {
          newTopicContentId = topic.content.contentId + 1;
        }
      });

      const newTopic = {
        hasError: false,
        isNewTopic: true,
        isDraftTopic: true,
        content: {
          contentId: newTopicContentId,
          contentThreadId: null,
          layoutType: 1,
          lastUpdated: Date.now(),
          title: '',
          panels: [],
        },
        libraryItemGroups: [],
      };

      setTopicsModel({
        newTopic,
        pagedTopics: topicsModel.pagedTopics,
      });
      setSelectedTopic(newTopic);
    }
  }, [topicsModel.newTopic, topicsModel.pagedTopics, topics]);

  const showEmptyModal = useCallback(() => {
    setShowEmptyClipboardModal(true);
  }, [setShowEmptyClipboardModal]);

  const editorContainer = document.getElementById(editorContainerID);
  const contextStatusInfoItem = useSyncedInfo();

  const setNewActiveEditor = useCallback(
    (editor) => {
      if (deactivateCallback) {
        deactivateCallback();
        setDeactivateCallback(null);
      }

      if (editorContainer && editor) {
        const insertingMiniTableRemoveListener = insertTablesAndCharts(
          editor,
          editorContainerID,
          projectId,
          (libraryItem) => {
            // eslint-disable-next-line no-underscore-dangle
            window.__LIBRARYITEMSTORE__.add({
              ...libraryItem,
              type: libraryItem.contentType,
            });
          },
          dispatch,
          contextStatusInfoItem,
          isPwcUser
        );

        editorContainer.addEventListener(
          `inserting-minitable-no-data`,
          showEmptyModal,
          false
        );

        setDeactivateCallback(() => {
          return () => {
            insertingMiniTableRemoveListener();
            editorContainer.removeEventListener(
              `inserting-minitable-no-data`,
              showEmptyModal,
              false
            );
          };
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deactivateCallback, editorContainer, projectId, showEmptyModal, isPwcUser]
  );

  const deleteSavedTopic = (topic) => {
    dispatch(
      deleteTopicById({
        projectId,
        topicThreadId: topic.content.contentThreadId,
      })
    )
      .unwrap()
      .then(() => {
        dispatch(removeTopic(topic.content.contentThreadId));
        setSelectedTopic(null);
        setDeleteConfirmationVisible(false);
      });
  };

  const handleDeleteTopic = async (topicIDToDelete) => {
    if (!_isNil(topicIDToDelete)) {
      try {
        const topicHelper = getTopicToUpdate(topicIDToDelete);

        if (!topicHelper) {
          throw new Error('Failed to delete topic.');
        }

        const { isNewTopic, topic, remainingLayouts } = topicHelper;

        if (isNewTopic) {
          // Local state
          setTopicsModel({
            newTopic: null,
            pagedTopics: topicsModel.pagedTopics,
          });
          setTopicsLayout(remainingLayouts);
          setSelectedTopic(null);
          setDeleteConfirmationVisible(false);
        }

        if (topic && topic?.content?.contentThreadId) {
          deleteSavedTopic(topic);
        }
      } catch (error) {
        throw new Error('Failed to delete topic.');
      }
    } else {
      throw new Error('Failed to delete topic.');
    }
  };

  const updateTopicVersion = (topic, versionStatus, contentId) => {
    const contentThreadId = topic.content
      ? topic.content.contentThreadId
      : topic.contentThreadId;

    getTopic(projectId, contentThreadId, contentId, versionStatus).then(
      (updatedTopic) => {
        if (updatedTopic) {
          const updatedTopics = [...topicsModel.pagedTopics];
          const existing = updatedTopics.find(
            (x) =>
              ((x.content && x.content.contentThreadId) ||
                x.contentThreadId) === contentThreadId
          );
          const existingNew =
            (topicsModel.newTopic?.content.contentThreadId ||
              topicsModel.newTopic?.contentThreadId) === contentThreadId
              ? topicsModel.newTopic
              : undefined;

          if (existing) {
            const index = updatedTopics.indexOf(existing);
            updatedTopics[index] = updatedTopic;
            setTopicsModel({
              newTopic: topicsModel.newTopic,
              pagedTopics: updatedTopics,
            });
            dispatch(
              updateTopic({
                id: contentThreadId,
                changes: updatedTopic,
              })
            );
          } else if (existingNew) {
            setTopicsModel({
              newTopic: updatedTopic,
              pagedTopics: topicsModel.pagedTopics,
            });
          } else {
            throw new Error("Couldn't find topic to update!");
          }
        } else {
          throw new Error("Couldn't get topic version!");
        }
      }
    );
  };

  const moveTopic = async (topic, offset) => {
    const moveDirection = offset === -1 ? 1 : 2;

    if (!topic) {
      throw new Error('Failed to move topic.');
    }

    const topicHelper = getTopicToUpdate(topic.content.contentId);

    if (!topicHelper) {
      throw new Error('Failed to find topic to move.');
    }

    const { allTopics } = topicHelper;

    const currentTopics = cloneDeep(allTopics);
    const existingIndex = currentTopics.findIndex(
      (x) =>
        ((x.content && x.content.contentThreadId) || x.contentThreadId) ===
        ((topic.content && topic.content.contentThreadId) ||
          topic.contentThreadId)
    );

    if (existingIndex > -1) {
      const newIndex = existingIndex + offset;

      if (newIndex >= 0 && newIndex < currentTopics.length) {
        [currentTopics[existingIndex], currentTopics[newIndex]] = [
          currentTopics[newIndex],
          currentTopics[existingIndex],
        ];
        try {
          dispatch(
            moveTopicById({
              projectId,
              topicThreadId: topic.content.contentThreadId,
              moveDirection,
            })
          )
            .unwrap()
            .then(() => {
              setTopicsModel({
                newTopic: topicsModel.newTopic,
                pagedTopics: currentTopics,
              });
            });
        } catch (error) {
          dispatch(
            showModalError(Constants.ModalErrorType.UNABLE_TO_SAVE_CHANGE)
          );
        }
      }
    }
  };

  const getMoveOptionForTopic = (index) => {
    if (topicsModel.pagedTopics.length === 1) {
      return Constants.MOVE_OPTIONS.NONE;
    }
    const firstNonDraftElementIndex = topics.findIndex(
      (topic) => !topic.isDraftTopic
    );
    let selectedMoveOption;

    if (index === 0 || index === firstNonDraftElementIndex) {
      selectedMoveOption = Constants.MOVE_OPTIONS.NEXT;
    } else if (index === topicsModel.pagedTopics.length - 1) {
      selectedMoveOption = Constants.MOVE_OPTIONS.PREV;
    } else {
      selectedMoveOption = Constants.MOVE_OPTIONS.BOTH;
    }
    return selectedMoveOption;
  };

  if (
    !isPwcUser &&
    topicsModel.pagedTopics &&
    !topicsModel.pagedTopics.length
  ) {
    return null;
  }

  const handleRightPanelOpen = (layout) => {
    setShowRightPanel(layout);
  };

  const handleRightPanelCollapse = () => {
    setShowRightPanel(false);
  };

  const handleTopicLayoutChange = (layout) => {
    setTopicsLayout(
      _defaultsDeep(
        {
          [selectedTopic?.content.contentId]: layout,
        },
        topicsLayout
      )
    );
    setShowRightPanel(layout);
  };

  const handleMoveTopic = (topic) => (offset) => moveTopic(topic, offset);

  const handleTriggerModalDeleteTopic = (topic) => () => {
    setSelectedTopic(topic);
    setDeleteConfirmationVisible(true);
  };

  const handleDeleteTopicConfirm = async () => {
    await handleDeleteTopic(selectedTopic?.content.contentId);
  };

  const deleteDraftNewTopic = (contentId) => {
    return handleDeleteTopic(contentId);
  };

  const handleTriggerContentEditTopic = (topic) => () =>
    setSelectedTopic(topic);

  const handleUpdateTopicStatus = (updatedTopic, versionStatus, contentId) =>
    updateTopicVersion(updatedTopic, versionStatus, contentId);

  const handleViewSpecificVersion = (topic) => (versionStatus, contentId) =>
    updateTopicVersion(topic, versionStatus, contentId);

  const resetPanelLayout = () => {
    const clonedTopicsLayout = cloneDeep(topicsLayout);
    delete clonedTopicsLayout[selectedTopic?.content.contentId];
    setTopicsLayout(clonedTopicsLayout);
  };

  const handleNext = () => !topicsIsLoading && fetchPagedTopics(itemPage + 1);

  const handleSaveExistings = (contentId, content) => {
    const topicHelper = getTopicToUpdate(contentId);

    if (!topicHelper) {
      throw new Error('Failed to save topic.');
    }

    const newTopicModel = {
      newTopic: topicsModel.newTopic,
      pagedTopics: topicsModel.pagedTopics.map((topic) => {
        if (topic.content.contentId === contentId) {
          return {
            ...topic,
            content: {
              ...topic.content,
              ...content,
            },
          };
        }

        return topic;
      }),
    };

    setTopicsModel(newTopicModel);
  };

  const handleSave = (
    contentId,
    newContentThreadId,
    changes,
    versionStatus = 1,
    versionNumber = 1,
    userViewStatus = 1
  ) => {
    const topicHelper = getTopicToUpdate(contentId);

    if (!topicHelper) {
      throw new Error('Failed to save topic.');
    }

    const { topic, remainingLayouts } = topicHelper;

    const createdTopic = _defaultsDeep(
      {
        content: _defaultsDeep(
          {
            contentId: newContentThreadId,
            contentThreadId: newContentThreadId,
            versionStatus,
            versionNumber,
            userViewStatus,
            dateModified: new Date().toISOString(),
            modifiedBy: loggedInUser.name,
          },
          changes.content
        ),
        libraryItemGroups: changes.libraryItemGroups,
        isDraftTopic: featureFlags.isAutoSaveEnabled, // when feature flag is enabled the newly created topic should be in edit mode
      },
      topic
    );

    delete createdTopic.isNewTopic;

    const newPagedTopics = [createdTopic, ...topicsModel.pagedTopics];

    setTopicsModel({
      newTopic: null,
      pagedTopics: newPagedTopics,
    });

    dispatch(addTopic(createdTopic));
    setItemCount(newPagedTopics.length);
    setTopicsLayout(remainingLayouts);

    if (
      createdTopic.content.panels.length &&
      (createdTopic.content.panels[0].type ===
        topicPanelType.TableauReportPage ||
        createdTopic.content.panels[0].type === topicPanelType.TableauReport)
    ) {
      dispatch(removeAllTopics());
      setTopicsModel({
        newTopic: null,
        pagedTopics: [],
      });
      setTopicsLayout({});
      fetchPagedTopics(itemPage);
    }
  };

  const handleError = (contentId) => {
    if (topicsModel.newTopic?.content.contentId === contentId) {
      setTopicsModel({
        newTopic: {
          ...topicsModel.newTopic,
          hasError: true,
        },
        pagedTopics: topicsModel.pagedTopics,
      });
    }
  };

  const handleCancel = (contentId) => {
    if (
      topicsModel.newTopic?.content.contentId === contentId &&
      topicsModel.newTopic?.hasError
    ) {
      setTopicsModel({
        newTopic: null,
        pagedTopics: topicsModel.pagedTopics,
      });
    }
  };

  const areTopicsPresent = topics && topics.length > 0;

  const ScrollLoader = () => (
    <Loader
      className={isTabletOrMobile ? 'small' : ''}
      data-testid="infite-scroll-loader"
    />
  );

  if (topicsHasError) {
    return <LoadingError />;
  }

  return (
    <div
      className="project-overview-module-container"
      data-testid="project-overview-module-container"
    >
      <ModalConfirmation
        show={isDeleteConfirmationVisible}
        onCancel={() => setDeleteConfirmationVisible(false)}
        onConfirm={handleDeleteTopicConfirm}
        confirmTitle="Are you sure you want to delete?"
        confirmationMessage={`By choosing Delete the “${selectedTopic?.content.title}” will be permanently deleted from “${projectName}” project`}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
      <div
        className="project-overview-header-container"
        data-testid="project-overview-header-container"
      >
        <div className="project-overview-label">
          <Icon
            data-testid="project-overview-icon"
            aria-label="project overview icon"
            name="grid"
            width={32}
            className="outline project-overview-icon"
          />
          <strong>Project Overview Topics</strong>
        </div>
        {areTopicsPresent && (
          <div>
            <ShowForPermissions
              projectId={projectId}
              permissions={[Constants.USER_PERMISSIONS.CONTRIBUTE]}
            >
              <EnableForProjectState projectId={projectId}>
                {({ isProjectDisabled }) => (
                  <Button
                    className="button-link"
                    onClick={handleOnAddNewTopic}
                    data-testid="button-add-topic"
                    aria-label="add topic"
                    type="button"
                    icon="add"
                    iconClasses={classnames('fill add-topic-icon', {
                      'icon-grey':
                        isProjectDisabled || sharedTopics || isMobile,
                    })}
                    disabled={isProjectDisabled || sharedTopics || isMobile}
                  >
                    Add topic
                  </Button>
                )}
              </EnableForProjectState>
            </ShowForPermissions>
          </div>
        )}
      </div>
      {areTopicsPresent ? (
        <>
          <div id={editorContainerID} />
          <RTEContextProvider
            value={{
              setEditor: (e) => {
                setNewActiveEditor(e);
              },
            }}
          >
            <InfiniteScroll
              dataLength={topics.length}
              hasMore={hasMoreItems()}
              next={handleNext}
              scrollThreshold={scrollThreshold}
              loader={<ScrollLoader />}
            >
              <AutoLock projectId={projectId} contentThreadIds={ids}>
                {topics.map((topic, index) => {
                  const { isNewTopic = false } = topic;
                  if (featureFlags.isAutoSaveEnabled && isNewTopic) {
                    const { content: { contentId: id } = {} } = topic;
                    return (
                      <AutoCreateTopic
                        key={id}
                        projectId={projectId}
                        id={id}
                        onSave={handleSave}
                        onError={handleError}
                        onCancel={handleCancel}
                      />
                    );
                  }

                  const currentTopicLayout =
                    topicsLayout[topic.content.contentId] ||
                    topic.content.layoutType;

                  const mappedTopic = mapOnlyPowerBiTopic(topic);
                  const moveOptions = getMoveOptionForTopic(index);
                  return (
                    <ProjectOverviewModule
                      key={topic.content.contentId}
                      isNewTopic={isNewTopic}
                      topic={mappedTopic}
                      topicLayout={currentTopicLayout}
                      libraryItemGroups={topic.libraryItemGroups}
                      projectId={projectId}
                      onDelete={handleTriggerModalDeleteTopic(topic)}
                      onEdit={handleTriggerContentEditTopic(topic)}
                      onTopicStatusUpdated={handleUpdateTopicStatus}
                      onViewSpecificVersion={handleViewSpecificVersion(topic)}
                      onMove={handleMoveTopic(topic)}
                      moveOption={moveOptions}
                      handleOpenPanel={handleRightPanelOpen}
                      onSelectionChange={setSelectedTopic}
                      resetPanelLayout={resetPanelLayout}
                      onSave={handleSave}
                      onUpdate={handleSaveExistings}
                      showEmptyClipboardModal={showEmptyClipboardModal}
                      setShowEmptyClipboardModal={setShowEmptyClipboardModal}
                      onDeleteDraftTopic={deleteDraftNewTopic}
                      isCreatingOrUpdating={isCreatingTopic || isUpdatingTopic}
                      isOnClientVersion={sharedTopics}
                    />
                  );
                })}
              </AutoLock>
            </InfiniteScroll>
          </RTEContextProvider>
        </>
      ) : (
        <div className="project-overview-module">
          <p className="project-overview-placeholder-text">
            {projectOverviewText}
          </p>
          <ShowForPermissions
            projectId={projectId}
            permissions={[Constants.USER_PERMISSIONS.CONTRIBUTE]}
          >
            <EnableForProjectState projectId={projectId}>
              {({ isProjectDisabled }) => (
                <Button
                  className="add-topic-button"
                  type="submit"
                  data-testid="button-add-topic"
                  aria-label="add topic"
                  onClick={handleOnAddNewTopic}
                  icon="add"
                  iconClasses={classnames('fill add-topic-icon', {
                    'icon-grey': isProjectDisabled || sharedTopics,
                  })}
                  disabled={isProjectDisabled || sharedTopics}
                >
                  Add topic
                </Button>
              )}
            </EnableForProjectState>
          </ShowForPermissions>
        </div>
      )}
      {showRightPanel && (
        <RightPanel
          activeLayoutId={showRightPanel}
          handleCollapse={handleRightPanelCollapse}
          handleLayoutTypeChange={handleTopicLayoutChange}
          layouts={TOPIC_LAYOUTS}
          isPoTopicLayout
        />
      )}

      <PowerPivotNotSupportedModal
        title={t(UNABLE_TO_ADD_CONTENT)}
        message={t(TEXT_AREA)}
      />
    </div>
  );
};

export default ProjectOverviewModuleContainer;
